import { useState, useRef, useEffect } from 'react';
import { getEntry, createEntry, addWorkspaceEntryRef, deleteWorkspaceEntryRef } from 'lib/api';
import useIsMobile from 'hooks/useIsMobile';
import WorkspaceDesktop from './WorkspaceDesktop';
import WorkspaceMobile from './WorkspaceMobile';
import { EntryType, EntryModel, WorkspaceData, WorkspaceEntryRef } from 'lib/models';

const INIT_Z_INDEX = 1000;

interface WorkspaceProps {
  uuid: string;
  data: WorkspaceData;
}

function Workspace(props: WorkspaceProps) {
  const [entryRefsMap, setEntryRefsMap] = useState<{[key: string]: WorkspaceEntryRef}>({});
  const [entriesMap, setEntriesMap] = useState<{[key: string]: EntryModel}>({});

  const isMobile = useIsMobile();

  const [offset, setOffset] = useState({x: 0, y: 0});
  const [zoom, setZoom] = useState<number>(1);
  const [isEmpty, setIsEmpty] = useState<boolean>(true);

  const zIndexCounterRef = useRef<number>(INIT_Z_INDEX);
  const isInitialLoad = useRef<string|null>(null);

  const onCreateNewEntry = async (
    entryType: EntryType,
    x: number,
    y: number,
  ): Promise<{ entry: EntryModel, ref: WorkspaceEntryRef }|undefined> => {
    const entry = await createEntry(entryType);
    const entryRef = await addWorkspaceEntryRef(
      props.uuid,
      entryType,
      entry.uuid,
      x,
      y,
      ++zIndexCounterRef.current,
    );

    setEntryRefsMap({...entryRefsMap, [entryRef.refId]: entryRef });
    entriesMap[entry.uuid] = entry;
    setIsEmpty(false);
    return { entry, ref: entryRef };
  };

  const onArchiveEntry = async (entryRefId: string, entryUuid: string) => {
    await deleteWorkspaceEntryRef(props.uuid, entryRefId);
    const entryRefs = {...entryRefsMap};
    delete entryRefs[entryRefId];
    delete entriesMap[entryUuid];
    setEntryRefsMap(entryRefs);
    setIsEmpty(!!Object.values(entriesMap).length);
  };

  useEffect(() => {
    const loadEntries = async () => {
      if (isInitialLoad.current == props.uuid) return;
      isInitialLoad.current = props.uuid;

      const workspace = await getEntry(props.uuid, EntryType.WORKSPACE);
      const data = workspace.data as WorkspaceData;
      const entries = await Promise.all(
        Object.values(data.entryRefs).map((x: WorkspaceEntryRef) => {
          zIndexCounterRef.current = Math.max(zIndexCounterRef.current, x.zIndex);
          return getEntry(x.entryUuid, x.entryType);
        })
      );
      setEntryRefsMap(props.data.entryRefs);

      const entriesMap = entries.reduce((dict, obj) => {
        dict[obj.uuid] = obj;
        return dict;
      }, {} as { [key: string]: EntryModel });
      setEntriesMap(entriesMap);
      setIsEmpty(!!Object.values(entriesMap).length);
    };

    if (props.data) {
      loadEntries();
    }
  }, [props.uuid, props.data.entryRefs]);

  return (
    isMobile ? (
      <WorkspaceMobile
        workspaceUuid={props.uuid}
        entriesMap={entriesMap}
        entryRefsMap={entryRefsMap}
        onCreateNewEntry={onCreateNewEntry}
        onArchiveEntry={onArchiveEntry}
      />
    ) : (
      <WorkspaceDesktop
        workspaceUuid={props.uuid}
        entriesMap={entriesMap}
        entryRefsMap={entryRefsMap}
        onCreateNewEntry={onCreateNewEntry}
        onArchiveEntry={onArchiveEntry}
      />
    )
  );
}

export default Workspace;
